import React, { useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import { CarouselProvider, Slider, Slide } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

// Types
import { ReviewTemplateTypeType } from 'Shared/types/ReviewTemplates.type';
import { FormTypeType } from 'Shared/types/ReviewFormTemplate.type';

// Contants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';
import QueryParamsConstants from 'Shared/constants/QueryParams.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useReviewTemplate } from 'Shared/utils/hooks/use-review-template';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpPageLayout, WpPageHeader, WpPageTitle, WpPageBody } from 'Wpds/components/page-layout';
import ReviewTemplateBuilderDetails from './_partials/ReviewTemplateBuilderDetails';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import ReviewFormTemplateListItem from './_partials/ReviewFormTemplateListItem';
import DeleteReviewTemplateModal from 'Shared/components/DeleteReviewTemplateModal/DeleteReviewTemplateModal';
import ReviewTemplateProvider from './ReviewTemplateContext';
import EditFormTemplateItem from './_partials/EditFormTemplateItem';

// Styles
import './ReviewTemplateBuilderPage.scss';

const ReviewTemplateBuilderPageV2 = () => {
  const { slug } = useParams();
  const history = useHistory();
  const { state } = useLocation();

  const callbackUrl = state?.[QueryParamsConstants.CALLBACK_URL] || null;

  const { isFetching, template, isCurrentUserCreator, handleDeleteReviewTemplate } = useReviewTemplate({
    slug,
  });
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [currentFormType, setCurrentFormType] = useState<FormTypeType>(null);

  const currentSlide = currentFormType ? 1 : 0;

  const handleCloseClick = () => {
    // if a callbackurl is pushed into the history state,
    // redirect to it, instead of gallery pages
    // i.e: creating a new template during the new review cycle flow
    if (callbackUrl) {
      history.push(callbackUrl);
      return;
    }
    if (template.templateType === ReviewTemplateTypeType.feedback) history.push(PageUrlsConstants.COACHING_FEEDBACK);
    if (template.templateType === ReviewTemplateTypeType.review360) history.push(PageUrlsConstants.COACHING_360);
    if (template.templateType === ReviewTemplateTypeType.performanceReview)
      history.push(PageUrlsConstants.PERFORMANCE_REVIEWS_ADMIN_TEMPLATES);
  };

  const handleDeleteTemplateClick = () => {
    handleDeleteReviewTemplate().then(() => {
      template.templateType === ReviewTemplateTypeType.performanceReview
        ? history.push(PageUrlsConstants.PERFORMANCE_REVIEWS_ADMIN_TEMPLATES)
        : history.push(PageUrlsConstants.COACHING_360);
    });
  };

  const handleChangeView = (formType?: FormTypeType) => {
    setCurrentFormType(formType || null);
  };

  // TODO: show loading state
  if (isFetching || !template) return null;

  const baseClassName = 'ReviewTemplateBuilderPage';

  return (
    <ReviewTemplateProvider>
      <WpPageLayout>
        <WpPageHeader slim width="container" sticky>
          <WpPageTitle
            title={template?.name || 'Untitled template'}
            buttonsContainer={
              !currentFormType && (
                <WpButtonGroup>
                  {isCurrentUserCreator && (
                    <WpButton variant="tertiary" onClick={toggleIsDeleteModalOpen}>
                      <WpIcon name="trash" />
                    </WpButton>
                  )}
                  <WpButton variant="tertiary" onClick={handleCloseClick}>
                    Save & Exit
                  </WpButton>
                </WpButtonGroup>
              )
            }
            buttonsOnMobile="inline"
          />
        </WpPageHeader>
        <WpPageBody width="container">
          <CarouselProvider
            currentSlide={currentSlide}
            naturalSlideWidth={100}
            naturalSlideHeight={125}
            totalSlides={2}
            dragEnabled={false}
            touchEnabled={false}
            disableKeyboard={true}
            isIntrinsicHeight
            className={`${baseClassName}__carousel`}
          >
            <Slider className={`${baseClassName}__content`}>
              <Slide index={0} className={`${baseClassName}__carousel__slider__slide`}>
                <div className={`${baseClassName}__content__main`}>
                  <ReviewTemplateBuilderDetails slug={slug} />
                  {template.reviewFormTemplates
                    .sort(a => (a.formType === FormTypeType.self_peer_review ? -1 : 0))
                    .map(reviewFormTemplate => (
                      <ReviewFormTemplateListItem
                        reviewFormTemplate={reviewFormTemplate}
                        setActiveSlide={handleChangeView}
                        key={`template-questions-${reviewFormTemplate.formType}`}
                      />
                    ))}
                </div>
              </Slide>
              <Slide index={1} className={`${baseClassName}__carousel__slider__slide`}>
                {currentFormType && (
                  <EditFormTemplateItem
                    handleChangeView={handleChangeView}
                    reviewFormType={currentFormType}
                    reviewFormTemplate={template.reviewFormTemplates.find(
                      template => template.formType === currentFormType
                    )}
                  />
                )}
              </Slide>
            </Slider>
          </CarouselProvider>
        </WpPageBody>
      </WpPageLayout>
      <DeleteReviewTemplateModal
        isOpen={isDeleteModalOpen}
        onToggle={toggleIsDeleteModalOpen}
        handleDelete={handleDeleteTemplateClick}
      />
    </ReviewTemplateProvider>
  );
};

export default ReviewTemplateBuilderPageV2;
